import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import {
  convertDatesInObject,
  insertSingleAtBeginningOfPage,
} from '@/helpers/api'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { templatesQueryKeys } from '@/hooks/templates/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { CreateTemplate, Template } from '@/types/library/Template'

interface UseCreateTemplateArgs {
  orgId: UUID | ''
}

const createTemplate = async (
  orgId: UUID | '',
  template: CreateTemplate,
  accessToken: string | undefined | null,
): Promise<Template> => {
  if (!accessToken) {
    throw new Error('No access token provided.')
  }
  if (!orgId) {
    throw new Error('No org ID provided.')
  }
  const body: CreateTemplate = {
    name: template.name,
    unrestricted: template.unrestricted,
  }
  if (template.content) {
    body.content = template.content
  }
  if (template.unrestricted !== undefined) {
    body.unrestricted = template.unrestricted
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/templates`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as Template
}

export const useCreateTemplate = ({ orgId }: UseCreateTemplateArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<Template, Error, CreateTemplate, void>({
    mutationFn: (template) => createTemplate(orgId, template, accessToken),
    onError: (err, template, context) => {
      // rollback to the previous value
      // TODO: figure out how to properly handle this error, since it propagates to the user
      const key = templatesQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data, template) => {
      const key = templatesQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<Template>>(key, (oldData) =>
        insertSingleAtBeginningOfPage(data, oldData),
      )
      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
